import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Share,
  CopyToClipboard,
} from '@uncinc/uncinc-react-kitchen-sink';

import './style.scss';

const ShareLinks = () => {
  const { t } = useTranslation();
  const url = typeof window !== 'undefined' && window.location.href || '';
  const text = t('general.share_article');

  return (
    <Share
      title={t('general.share_article')}
      withLabels
      items={['facebook', 'linkedin', 'whatsapp', 'bluesky', 'signal', 'mail', 'copy']}
      customItems={{
        copy: (<CopyToClipboard
          icon="attachment"
          text={window.location.href}
          copyText={t('general.copy')}
          copiedText={t('general.copied')}
        />),
        bluesky: (
          <CustomShareLink
            baseUrl="https://bsky.app/intent/compose?text="
            iconClass="icon-bluesky"
            labelKey="general.share_bluesky"
            url={url}
            text={text}
          />
        ),
        signal: (
          <SignalShareLink
            iconClass="icon-signal"
            labelKey="general.share_signal"
            url={url}
            text={text}
          />
        ),
      }}
    />
  );
};

export default ShareLinks;

const CustomShareLink = ({ baseUrl, iconClass, labelKey, url, text }) => {
  const { t } = useTranslation();
  const shareUrl = `${baseUrl}${encodeURIComponent(`${text} ${url}`)}`;

  return (
    <div className="Share__item-wrapper">
      <a
        href={shareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`Share__item ${iconClass.replace('icon-', '')}`}
      >
        <span className={`icon ${iconClass}`} />
        <span className="Share__item__label">{t(labelKey)}</span>
      </a>
    </div>
  );
};

/**
 * SignalShareLink Component
 *
 * A share button component that attempts to use the native Web Share API when available,
 * falling back to clipboard functionality when not supported. Designed specifically for
 * Signal sharing but works as a general share button.
 *
 * @param {Object} props - Component props
 * @param {string} props.iconClass - CSS class for the share icon (e.g., 'icon-signal')
 * @param {string} props.labelKey - Translation key for the button label
 * @param {string} props.url - URL to be shared
 * @param {string} props.text - Text/title to be shared along with the URL
 *
 * @returns {JSX.Element} A clickable share button element
 */
const SignalShareLink = ({ iconClass, labelKey, url, text }) => {
  const { t } = useTranslation();

  /**
   * Handles the share action with progressive fallback strategy:
   * 1. Try native Web Share API if available
   * 2. Fall back to clipboard copy with permission check
   * 3. Gracefully handle unsupported scenarios
   *
   * @param {Event} e - Click event object
   * @returns {Promise<void>} Promise that resolves when share action is complete
   */
  const handleSignalShare = async (e) => {
    e.preventDefault();

    // Primary: Try Web Share API (available on mobile and some desktop browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: text,
          url: url,
        });
        return;
      } catch (err) {
        // User cancelled or share failed - no action needed
        // This is expected behavior when user cancels the native share dialog
        return;
      }
    }

    // Fallback: Copy to clipboard when Web Share API is not available
    await handleClipboardFallback();
  };

  /**
   * Fallback method to copy content to clipboard when Web Share API is unavailable.
   * Includes permission checking to prevent errors in restricted environments like Android WebViews.
   *
   * @see https://issues.chromium.org/issues/40418964
   * @returns {Promise<void>} Promise that resolves when clipboard operation is complete
   */
  const handleClipboardFallback = async () => {
    // Check if permissions API is available to prevent errors in Android WebViews
    if (typeof navigator?.permissions === 'undefined') {
      showUnsupportedMessage();
      return;
    }

    try {
      // Check clipboard write permissions
      const result = await navigator.permissions.query({ name: 'clipboard-write' });
      const hasPermission = result?.state && ['granted', 'prompt'].includes(result.state);

      if (hasPermission && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        // eslint-disable-next-line no-alert
        alert(t('general.copied_as_alternative'));
      } else {
        showUnsupportedMessage();
      }
    } catch (error) {
      showUnsupportedMessage();
    }
  };

  /**
   * Shows a message when sharing/copying is not supported in the current environment
   *
   * @returns {void}
   */
  const showUnsupportedMessage = () => {
    // eslint-disable-next-line no-alert
    alert(t('general.share_not_supported'));
  };

  return (
    <div className="Share__item-wrapper">
      <a
        onClick={handleSignalShare}
        className={`Share__item ${iconClass.replace('icon-', '')}`}
        type="button"
        aria-label={t(labelKey)}
      >
        <span className={`icon ${iconClass}`} />
        <span className="Share__item__label">{t(labelKey)}</span>
      </a>
    </div>
  );
};
